'use client';

import 'dayjs/locale/ar';
import 'dayjs/locale/ur';

import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  Box,
  Paper,
  Popper,
  TextField,
  Typography,
  ListItemButton,
  ClickAwayListener,
} from '@mui/material';

import { formatTimeSlot, formatDateLabel } from 'src/utils/helper';

import { useLocale, useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

dayjs.extend(localeData);
dayjs.extend(customParseFormat);

const SlotSelector = ({ slotsList = [], label, onChange, error, helperText, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const { currentLang } = useTranslate();
  const { t } = useLocale();

  useEffect(() => {
    dayjs.locale(currentLang.value);
  }, [currentLang.value]);

  useEffect(() => {
    if (slotsList?.length > 0) {
      const firstDate = slotsList[0];
      const firstSlot = firstDate.timeSlots?.[0];
      if (firstSlot) {
        const autoSelect = {
          date: firstDate.value,
          slot: firstSlot.value,
        };
        setSelectedSlot(autoSelect);
        onChange?.(autoSelect);
      }
    } else {
      setSelectedSlot(null);
      //   onChange?.(null);
    }
  }, [slotsList]);

  const handleDateSelect = (index) => {
    setSelectedDateIndex(index);
    const dateItem = slotsList[index];
    const firstSlot = dateItem?.timeSlots?.[0];
    if (firstSlot) {
      const autoSelect = {
        date: dateItem.value,
        slot: firstSlot.value,
      };
      setSelectedSlot(autoSelect);
      onChange?.(autoSelect);
    }
  };

  const handleSlotSelect = (slot) => {
    const dateItem = slotsList[selectedDateIndex];
    const selected = {
      date: dateItem.value,
      slot: slot.value,
    };
    setSelectedSlot(selected);
    onChange?.(selected);
    setAnchorEl(null);
  };

  const dateItem = slotsList[selectedDateIndex];
  const settings = JSON.parse(localStorage.getItem('app-settings') || '{}');
  const isDark = settings?.mode === 'dark';
  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <Box width="100%">
        <TextField
          fullWidth
          variant="outlined"
          label={label || t('Select_Date_Time')}
          placeholder={t('Select_Date_Time')}
          error={!!error}
          helperText={helperText}
          disabled={disabled}
          slotProps={{
            formHelperText: { sx: { ml: 0, mt: 0 } },
          }}
          value={
            selectedSlot
              ? `${formatDateLabel(selectedSlot.date)} - ${formatTimeSlot(selectedSlot.slot)}`
              : ''
          }
          onClick={(e) => setAnchorEl(e.currentTarget)}
          InputProps={{
            startAdornment: (
              <Iconify
                icon="line-md:calendar"
                width={20}
                height={20}
                sx={{ mr: 2, color: !disabled && 'primary.main' }}
              />
            ),
            readOnly: true,
          }}
        />

        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1500, width: { xs: '90%', md: 420 } }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Paper
            sx={{
              display: 'flex',
              width: '100%',
              maxHeight: 300,
              overflow: 'hidden',
              borderRadius: 1,
              boxShadow: 3,
            }}
          >
            {/* Left: Dates */}
            <Box sx={{ width: 180, borderRight: '1px solid #eee', overflowY: 'auto' }}>
              {slotsList.map((item, index) => (
                <ListItemButton
                  key={index}
                  selected={index === selectedDateIndex}
                  onClick={() => handleDateSelect(index)}
                  sx={(theme) => ({
                    py: 1.5,
                    px: 2,
                    justifyContent: 'flex-start',
                    color: theme.palette.text.primary, // auto changes in dark mode
                    borderRadius: 0,
                  })}
                >
                  <Typography
                    sx={(theme) => ({
                      ...theme.typography.body1,
                      fontSize: '0.90rem', // Matches MUI TextField label size
                      fontWeight: 400,
                      color: isDark ? theme.palette.grey[100] : theme.palette.text.primary, // Matches label default color
                      lineHeight: '1.4375em',
                      letterSpacing: '0.00938em',
                    })}
                  >
                    {formatDateLabel(item.value)}
                  </Typography>
                </ListItemButton>
              ))}
            </Box>

            {/* Right: Time Slots */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {(dateItem?.timeSlots || []).map((slot, idx) => {
                const isSelected =
                  selectedSlot?.slot === slot.value && selectedSlot?.date === dateItem.value;

                return (
                  <ListItemButton
                    key={idx}
                    onClick={() => handleSlotSelect(slot)}
                    selected={isSelected}
                    sx={(theme) => ({
                      py: 1.5,
                      px: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      justifyContent: 'flex-start',
                      backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
                      color: theme.palette.text.primary, // theme-based color
                    })}
                  >
                    <Typography
                      sx={(theme) => ({
                        ...theme.typography.body1,
                        fontSize: '0.90rem', // Matches MUI TextField label size
                        fontWeight: 400,
                        color: isDark ? theme.palette.grey[100] : theme.palette.text.primary, // Matches label default color
                        lineHeight: '1.4375em',
                        letterSpacing: '0.00938em',
                      })}
                      fontWeight={600}
                    >
                      {formatTimeSlot(slot.value)}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SlotSelector;
